import React, { useState } from "react";
import {
  Building2,
  UserCog,
  ShieldCheck,
  ArrowDownWideNarrow,
  RotateCcw,
  Search,
  Filter,
} from "lucide-react";

const TeamDropdownContent = ({ filters, setFilters, offices = [], roles = [], onApply }) => {
  const [searchTerm, setSearchTerm] = useState("");

  // 🧩 Determine if filters are empty
  const isFiltersEmpty =
    filters.offices.length === 0 &&
    filters.roles.length === 0 &&
    !filters.status &&
    !filters.sortBy &&
    !filters.searchTerm;

  // 🔹 Reset all selections
  const handleReset = () => {
    setFilters({
      offices: [],
      roles: [],
      status: "",
      sortBy: "",
      searchTerm: "",
    });
    setSearchTerm("");

    // tell parent to clear filters and show default list
    if (onApply) onApply(null);
  };

  // 🔹 Toggle selection
  const toggleValue = (category, value) => {
    setFilters((prev) => {
      switch (category) {
        case "Offices":
          return {
            ...prev,
            offices: prev.offices.includes(value)
              ? prev.offices.filter((v) => v !== value)
              : [...prev.offices, value],
          };
        case "Roles":
          return {
            ...prev,
            roles: prev.roles.some((r) => r.id === value.id)
              ? prev.roles.filter((r) => r.id !== value.id)
              : [...prev.roles, value],
          };
        case "Status":
          return { ...prev, status: value };
        case "Sort By":
          return { ...prev, sortBy: value };
        default:
          return prev;
      }
    });
  };

  // 🔹 Handle search term
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setFilters((prev) => ({ ...prev, searchTerm: value }));
  };

  // 🔹 Menu configuration
  const menu = [
    {
      label: "Offices",
      icon: <Building2 className="w-4 h-4 text-[#223688]" />,
      items: offices,
    },
    {
      label: "Roles",
      icon: <UserCog className="w-4 h-4 text-[#223688]" />,
      items: roles,
    },
    {
      label: "Status",
      icon: <ShieldCheck className="w-4 h-4 text-[#223688]" />,
      items: ["Active", "Blocked"],
    },
    {
      label: "Sort By",
      icon: <ArrowDownWideNarrow className="w-4 h-4 text-[#223688]" />,
      items: ["Name (A–Z)", "Recently Added"],
    },
  ];

  return (
    <div className="bg-gray-50 dark:bg-acs-dark-bg p-fluid-3 rounded-xl border border-gray-100 dark:border-acs-dark-border shadow-md w-[800px] max-h-[70vh] overflow-y-auto">
      {/* === Search Row + Buttons === */}
      <div className="flex justify-between items-center mb-fluid-3">
        {/* === Left side: Search bar === */}
        <div className="relative flex items-center border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface rounded-full px-3 py-1.5 w-[clamp(14rem,30vw,20rem)] shadow-sm">
          <input
            type="text"
            placeholder="Search Team"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full bg-transparent outline-none text-fluid-sm text-gray-800 dark:text-acs-dark-text"
          />
          <button
            onClick={() => onApply(filters)}
            className="absolute right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <Search size={18} strokeWidth={2} />
          </button>
        </div>

        {/* === Right side: Apply + Reset buttons === */}
        <div className="flex items-center gap-2">
          {/* Apply Filters */}
          <button
            onClick={() => onApply(filters)}
            disabled={isFiltersEmpty}
            className={`flex items-center gap-1 text-sm font-medium rounded-md px-4 py-1.5 shadow-sm transition
              ${
                isFiltersEmpty
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#223688] text-white hover:bg-[#1a2c73] cursor-pointer"
              }`}
          >
            <Filter className="w-4 h-4" strokeWidth={2} />
            Apply Filters
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            disabled={isFiltersEmpty}
            className={`flex items-center gap-1 text-sm font-medium border px-4 py-1.5 rounded-md transition
              ${
                isFiltersEmpty
                  ? "text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50"
                  : "text-[#223688] hover:text-[#1a2c73] border-gray-300 hover:border-[#223688]"
              }`}
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* === Grid Layout for Filter Sections === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-fluid-3">
        {menu.map((section) => (
          <div
            key={section.label}
            className="bg-white dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-lg p-fluid-2 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-fluid-2">
              {section.icon}
              <h3 className="text-fluid-base font-semibold text-gray-800 dark:text-acs-dark-text">
                {section.label}
              </h3>
            </div>

            <div className="flex flex-col gap-2 max-h-[40vh] overflow-y-auto pr-1">
              {section.items.map((opt) => {
                const label = section.label === "Roles" ? opt.name : opt;
                const isChecked =
                  (section.label === "Offices" && filters.offices.includes(opt)) ||
                  (section.label === "Roles" && filters.roles.some((r) => r.id === opt.id)) ||
                  (section.label === "Status" && filters.status === opt) ||
                  (section.label === "Sort By" && filters.sortBy === opt);

                const inputType =
                  section.label === "Status" || section.label === "Sort By"
                    ? "radio"
                    : "checkbox";

                return (
                  <label
                    key={section.label === "Roles" ? opt.id : opt}
                    className="flex items-center gap-2 text-sm text-gray-700 dark:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-[#2A2D35] rounded-md px-2 py-1 cursor-pointer transition"
                  >
                    <input
                      type={inputType}
                      checked={isChecked}
                      onChange={() => toggleValue(section.label, opt)}
                      name={section.label}
                      className="accent-[#223688] w-4 h-4 rounded-md"
                    />
                    <span className="truncate">
                      {label}
                      {section.label === "Roles" && (
                        <span className="text-xs text-gray-400 ml-1">({opt.id})</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamDropdownContent;
